import {
  AutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import {
  Switch,
  FormControlLabel,
  MenuItem,
  Grid,
  Typography
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import SectionTitle from '../../../components/SectionTitle'
import { ENTITY_LIST_QUERY } from '../../../graphql/entityList'
import {
  CreditorEntityRef,
  entitiesWithoutACreditor
} from '../../../helpers/creditorEntities'
import { addCommaToPrice, removeCommaFromPrice } from '../../../helpers/numbers'
import { getTranslate } from '../../../localization'
import { Currency } from '../../../types/currency'
import { EntityList } from '../../../types/entityList'
import { EntityType } from '../../../types/reserveMonitoringList'
import { WalletEntity } from '../../../types/wallet'
import {
  AddCreditorsRequestInput,
  CreditorInput
} from '../../../types/walletCreditor'
import useStyles from './styles/stepTwoCreditorsDetails.styles'

interface CreditorDetailsSectionProps {
  creditorsInput: AddCreditorsRequestInput
  setcreditorsInput: React.Dispatch<
    React.SetStateAction<AddCreditorsRequestInput>
  >
  currency: Currency
  index: number
  creditor: CreditorInput
  existingCreditors: CreditorEntityRef[]
}

const CreditorDetailsSection: React.FC<CreditorDetailsSectionProps> = ({
  creditorsInput,
  setcreditorsInput,
  currency,
  index,
  creditor,
  existingCreditors
}) => {
  const classes = useStyles()
  const [options, setOptions] = React.useState<EntityList[]>([])

  const [getEntityList, { loading: loadingForEntityList }] = useLazyQuery<{
    entityList: EntityList[]
  }>(ENTITY_LIST_QUERY, {
    onCompleted: (res) => {
      setOptions(res.entityList)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [AutocompleteWithLabelEntity, setAutocompleteWithLabelEntity] =
    useState<any>(null)

  // Function to handle changes in each creditor
  const handleCreditorChange = (i: number, field: string, value: any) => {
    const updatedCreditors = [...creditorsInput.creditors]
    updatedCreditors[i] = {
      ...updatedCreditors[i],
      [field]: value
    }
    setcreditorsInput((prev) => ({
      ...prev,
      creditors: updatedCreditors
    }))
  }

  // Function to handle changes in entity fields within each creditor
  const handleCreditorEntityChange = (i: number, entity: WalletEntity) => {
    const updatedCreditors = [...creditorsInput.creditors]
    updatedCreditors[i] = {
      ...updatedCreditors[i],
      entity
    }
    setcreditorsInput((prev) => ({
      ...prev,
      creditors: updatedCreditors
    }))
  }

  // A wallet holds at most one creditor per entity, so entities that already have one
  // cannot be added again — the server refuses them with "creditor already exists" and
  // no hint that the existing creditor can be edited instead. On stage that is the
  // common case: every wallet carries an `adjusment` creditor from a migration.
  // Rows other than this one: an entity picked in row 1 must not stay on offer in
  // row 2, since all rows go to the server in a single addCreditors call and the
  // second would fail with the same "creditor already exists" this change removes.
  const otherRows = creditorsInput.creditors.filter((_, i) => i !== index)

  const addableOptions = entitiesWithoutACreditor(
    options,
    existingCreditors,
    creditor.entity?.entityType,
    otherRows
  )

  useEffect(() => {
    // Fetch options when entityType is set in any creditor
    creditorsInput.creditors.forEach((crd) => {
      if (crd.entity?.entityType) {
        getEntityList({
          variables: {
            entityType: crd.entity.entityType
          }
        })
      }
    })
  }, [creditorsInput.creditors])

  return (
    <div className={classes.creditorSection}>
      <SectionTitle
        title={`${getTranslate('Creditor')} ${index + 1}`}
        marginBottom
      />

      <Grid container spacing={3}>
        {/* Entity Dropdown */}
        <Grid item xs={12} sm={4}>
          <SelectWithLabel
            label={getTranslate('Entity Type')}
            placeholder={getTranslate('Select the Entity Type')}
            fullWidth
            value={creditor.entity.entityType}
            onChange={(event) => {
              handleCreditorEntityChange(index, {
                ...creditor.entity,
                entityType: event.target.value as EntityType,
                entityCode: '',
                entityID: '',
                entityName: ''
              })
              setAutocompleteWithLabelEntity(null)
            }}
            size="small">
            {/*
              Provider only. Credit on a bilateral wallet is extended BY the provider whose
              rate plan is being booked: every booking handler resolves the creditor with
              GetCreditorByEntityAndWallet(walletID, providerCode, EntityProvider), and the
              wallet type is itself named ProviderCredit.

              A Channel option was offered here even though booking paths resolve Provider
              creditors exclusively. The wallet service now enforces the same invariant, so
              offering another type would only produce a form that fails on submit.
            */}
            <MenuItem key="Provider" value="Provider">
              {getTranslate('Provider')}
            </MenuItem>
          </SelectWithLabel>
        </Grid>

        {/* Entity Autocomplete */}
        <Grid item xs={12} sm={4}>
          <AutocompleteWithLabel
            value={AutocompleteWithLabelEntity}
            options={loadingForEntityList ? [] : addableOptions}
            inputSize="small"
            getOptionLabel={(option) => option.label}
            getOptionSelected={(option, value) =>
              option.label.toLowerCase().includes(value.label.toLowerCase())
            }
            onChange={(_, value: any) => {
              if (value) {
                handleCreditorEntityChange(index, {
                  ...creditor.entity,
                  entityID: value ? value.id : '',
                  entityCode: value ? value.entityCode : undefined,
                  entityName: value ? value.label : ''
                })
                setAutocompleteWithLabelEntity({
                  value: value.value,
                  code: value.entityCode,
                  id: value.id,
                  label: value.label
                })
              } else {
                handleCreditorEntityChange(index, {
                  ...creditor.entity,
                  entityID: '',
                  entityCode: '',
                  entityName: ''
                })
                setAutocompleteWithLabelEntity(null)
              }
            }}
            label={getTranslate('Entity')}
            loading={loadingForEntityList}
            loadingText={getTranslate('Loading') + '...'}
          />
        </Grid>
        {/* Currency Display */}
        <Grid item xs={12} sm={2} className={classes.currencyDisplay}>
          <Typography variant="body2" color="textSecondary">
            {getTranslate('Currency')}
          </Typography>
          <Typography variant="body1" color="primary">
            {currency || 'N/A'}
          </Typography>
        </Grid>

        {/* Max Credit Input */}
        <Grid item xs={12} sm={4}>
          <InputWithLabel
            label={getTranslate('Max Credit')}
            placeholder={getTranslate('Enter max credit')}
            autoComplete="off"
            value={addCommaToPrice(creditor.maxCredit)}
            onChange={(event) => {
              const commaLessValue = removeCommaFromPrice(
                event.currentTarget.value
              )
              handleCreditorChange(index, 'maxCredit', Number(commaLessValue))
            }}
            inputSize="small"
          />
        </Grid>

        {/* Max Reservable Input */}
        <Grid item xs={12} sm={4}>
          <InputWithLabel
            label={getTranslate('Max Reservable')}
            placeholder={getTranslate('Enter max reservable')}
            autoComplete="off"
            value={addCommaToPrice(creditor.maxReservable)}
            onChange={(event) => {
              const commaLessValue = removeCommaFromPrice(
                event.currentTarget.value
              )
              handleCreditorChange(
                index,
                'maxReservable',
                Number(commaLessValue)
              )
            }}
            inputSize="small"
          />
        </Grid>
        <Grid item xs={12} sm={4}></Grid>

        {/* Active Toggle */}
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={creditor.active}
                onChange={() =>
                  handleCreditorChange(index, 'active', !creditor.active)
                }
                color="primary"
              />
            }
            label={getTranslate('Active')}
          />
        </Grid>

        {/* Can Use Credit Toggle */}
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={creditor.canUseCredit}
                onChange={() =>
                  handleCreditorChange(
                    index,
                    'canUseCredit',
                    !creditor.canUseCredit
                  )
                }
                color="primary"
              />
            }
            label={getTranslate('Can use credit')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={creditor.autoSettelmentEnabled}
                onChange={() =>
                  handleCreditorChange(
                    index,
                    'autoSettelmentEnabled',
                    !creditor.autoSettelmentEnabled
                  )
                }
                color="primary"
              />
            }
            label={getTranslate('Enable Auto Settlement')}
          />
        </Grid>
      </Grid>
    </div>
  )
}

export default CreditorDetailsSection
